import React, { useState } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

const HoldingsTable = ({ holdings, onTrade, loading }) => {
  const [sortKey, setSortKey] = useState("value");
  const [sortDir, setSortDir] = useState("desc");

  const sorted = [...(holdings || [])]?.sort((a, b) => {
    const av = a?.[sortKey], bv = b?.[sortKey];
    return sortDir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  });

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {Array.from({ length: 4 })?.map((_, i) => (
          <div key={i} className="h-32 rounded-xl bg-card border border-border animate-pulse" />
        ))}
      </div>
    );
  }

  if (!sorted?.length) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Icon name="PieChart" size={40} color="var(--color-muted-foreground)" />
        <p className="mt-3 text-sm text-muted-foreground">No holdings yet. Start trading to build your portfolio.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Sort controls */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-xs text-muted-foreground">Sort by:</span>
        {[{ key: 'value', label: 'Value' }, { key: 'unrealizedPnl', label: 'P&L' }, { key: 'allocation', label: 'Allocation' }, { key: 'qty', label: 'Shares' }]?.map(s => (
          <button key={s?.key} onClick={() => handleSort(s?.key)}
            className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-all min-h-[28px] ${
              sortKey === s?.key ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-primary'
            }`}>
            {s?.label}
            {sortKey === s?.key && <Icon name={sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={10} color="currentColor" />}
          </button>
        ))}
      </div>

      {/* 2-column grid on desktop, 1-column on mobile */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {sorted?.map(h => (
          <div key={h?.id} className="rounded-xl border border-border bg-card p-4 hover:border-primary/30 transition-all">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center text-xs font-heading font-bold flex-shrink-0"
                  style={{ background: h?.color + '22', color: h?.color }}>
                  {h?.ticker?.slice(0, 2)}
                </div>
                <div>
                  <div className="font-heading text-sm font-bold text-foreground">{h?.ticker}</div>
                  <div className="font-caption text-xs text-muted-foreground">{h?.qty?.toLocaleString()} shares</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-data text-sm font-bold text-foreground">${h?.value?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                <div className="font-caption text-xs" style={{ color: h?.color }}>{h?.sector}</div>
              </div>
            </div>

            {/* Metrics grid */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="text-center">
                <div className="font-caption text-xs text-muted-foreground">Avg Cost</div>
                <div className="font-data text-xs font-semibold text-foreground">${h?.avgCost?.toFixed(2)}</div>
              </div>
              <div className="text-center">
                <div className="font-caption text-xs text-muted-foreground">Current</div>
                <div className="font-data text-xs font-semibold text-foreground">${h?.currentPrice?.toFixed(2)}</div>
              </div>
              <div className="text-center">
                <div className="font-caption text-xs text-muted-foreground">Alloc</div>
                <div className="font-data text-xs font-semibold text-foreground">{h?.allocation?.toFixed(1)}%</div>
              </div>
            </div>

            {/* P&L */}
            <div className={`flex items-center justify-between p-2 rounded-lg mb-3 ${
              h?.unrealizedPnl >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'
            }`}>
              <span className="font-caption text-xs text-muted-foreground">Unrealized P&L</span>
              <div className="text-right">
                <span className={`font-data text-xs font-bold ${
                  h?.unrealizedPnl >= 0 ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {h?.unrealizedPnl >= 0 ? '+' : ''}${h?.unrealizedPnl?.toFixed(2)}
                </span>
                <span className={`ml-1 font-caption text-xs ${
                  h?.unrealizedPnlPct >= 0 ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  ({h?.unrealizedPnlPct >= 0 ? '+' : ''}{h?.unrealizedPnlPct?.toFixed(2)}%)
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button variant="success" size="sm" fullWidth onClick={() => onTrade?.(h, 'buy')}>Buy More</Button>
              <Button variant="danger" size="sm" fullWidth onClick={() => onTrade?.(h, 'sell')}>Sell</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HoldingsTable;